import React, { useRef, useState, useContext } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  FormContext,
  useEventCallback,
  SnackbarContext,
  AbortableFetch,
  abortableFetch,
  PromptContext,
  usePrompts,
  NavigationContext,
} from '@eas/common-web';
import { ProcessActionsProps } from '../../../components/process/process-types';
import { EvidenceAPI, Messages } from '../../../enums';
import { IrzFacility } from '../../../models';
import { ProcessForm } from '../irz-facility-split-types';
import { getErrorMessage } from '../../../utils/get-message';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(1),
      float: 'right',
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
  })
);

interface SplitFacilityApiCall {
  originalFacility?: IrzFacility;
  newFacility?: Partial<IrzFacility>;
  note?: string;
}

/**
 * Api call
 *
 * @param body
 */
function callApi({
  originalFacility,
  newFacility,
  note,
}: SplitFacilityApiCall) {
  return abortableFetch(
    `${EvidenceAPI.IRZ_FACILITIES}/${originalFacility?.id}/split`,
    {
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      method: 'POST',
      body: JSON.stringify({
        originalFacility,
        newFacility,
        note,
      }),
    }
  );
}

const PROMPT_KEY = 'SPLIT_FACILITIES';

export function StepSummaryActions({
  handleNext,
  handleBack,
  handleExit,
  allValues,
  navigationPrompt,
}: ProcessActionsProps<ProcessForm>) {
  const classes = useStyles();

  /**
   * Loading state.
   */
  const [loading, setLoading] = useState(false);

  /**
   * Context stuff.
   */
  const { setFieldValue } = useContext(FormContext);
  const { testPrompt } = useContext(PromptContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const { unregisterPrompt } = useContext(NavigationContext);

  /**
   * Fetch reference.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Prompt dialog.
   */
  usePrompts([
    {
      key: PROMPT_KEY,
      dialogTitle: 'Rozdelění provozoven IRZ',
      dialogText: 'Skutečně chcete rozdelit zvolené provozovny IRZ?',
    },
  ]);

  const handleNextWithConfirm = useEventCallback(() => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: async () => {
        try {
          setLoading(true);
          if (fetch.current !== null) {
            fetch.current.abort();
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const {
            editData,
            activities,
            identificators,
            basicInfo,
            location,
            operator,
            note,
          } = allValues;
          const newFacility = {
            ...basicInfo,
            ...activities,
            ...identificators,
            ...location,
            operator,
          };

          fetch.current = callApi({
            newFacility,
            originalFacility: editData,
            note,
          });

          const splittedFacility: IrzFacility = await fetch.current.json();
          showSnackbar(...Messages.IrzFacility.SPLIT.SUCCESS);
          setFieldValue('splittedFacility', splittedFacility);
          setFieldValue('isSent', true);
          unregisterPrompt(navigationPrompt);

          handleNext();
        } catch (err) {
          setLoading(false);

          if (err.name !== 'AbortError') {
            const message = getErrorMessage(
              Messages.IrzFacility.SPLIT,
              err.code
            );

            showSnackbar(...message);

            throw err;
          }
        } finally {
          setLoading(false);
        }
      },
    });
  });

  return (
    <div className={classes.actionsContainer}>
      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextWithConfirm}
          className={classes.button}
          disableElevation
          disabled={loading}
          startIcon={
            loading && <CircularProgress size="20px" color="inherit" />
          }
        >
          Rozdělit provozovny
        </Button>
        <Button
          onClick={handleBack}
          className={classes.button}
          disableElevation
          disabled={loading}
        >
          Předchozí
        </Button>
        <Button
          onClick={handleExit}
          className={classes.button}
          disableElevation
        >
          Zrušit
        </Button>
      </div>
    </div>
  );
}
